import { AfterViewInit, Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Parameter } from 'src/app/models/parameter.model';
import { MeasurementModeEnum } from 'src/app/enums/measurementMode.enum';
import { ParameterService } from 'src/app/services/parameter.service';
import { CommonService } from 'src/app/services/common.service';
import { GridViewService } from 'src/app/services/grid-view.service';
import { ToastService } from 'src/app/services/toast.service';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { DeleteDialog } from 'src/app/shared/delete-dialog/delete-dialog.component';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { ParameterDialogComponent, ParameterDialogData } from 'src/app/modals/parameter-dialog/parameter-dialog.component';
import { ParameterDeleteBlockedDialogComponent } from 'src/app/modals/parameter-delete-blocked-dialog/parameter-delete-blocked-dialog.component';
import { AuthService } from 'src/app/services/auth.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { realtimeConfigRouteConfig } from 'src/app/shared/constants/realtimeConfigRouteConfig.constants';
import { TranslateService } from 'src/app/services/translate.service';
import { AveragingTypeEnum } from 'src/app/enums/averagingType.enum';
import { getNamedEnum } from 'src/app/_helpers/enum-helper';
import { ChartService } from 'src/app/services/chart.service';
import { SampleTypeEnum } from 'src/app/enums/sampleType.enum';
import { ParameterTypeEnum } from 'src/app/enums/parameterType.enum';

@Component({
  standalone: false,
  selector: 'app-parameters-list',
  templateUrl: './../../shared/grid/core-grid/core-grid.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./../../shared/grid/core-grid/core-grid.component.scss']
})
export class ParametersListComponent extends CoreGridComponent<Parameter> implements AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private readonly averagingTypeNames = getNamedEnum(AveragingTypeEnum);
  private readonly parameterTypeFilterMap: { value: number; name: string }[];

  constructor(
    protected toastService: ToastService,
    protected dialog: MatDialog,
    protected commonService: CommonService,
    protected gridViewService: GridViewService,
    protected authService: AuthService,
    protected gridSettingsService: GridSettingsService,
    private readonly parameterService: ParameterService,
    private readonly translateService: TranslateService,
    private readonly chartService: ChartService
  ) {
    super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
    this.gridService = this.parameterService;
    this.parameterTypeFilterMap = Object.values(ParameterTypeEnum)
      .filter((value): value is number => typeof value === 'number')
      .map((value) => ({
        value,
        name: this.chartService.getParameterName(value) ?? String(ParameterTypeEnum[value])
      }));
  }

  protected get objectId(): string {
    return RolePermissionObjects.Parameter;
  }

  protected override get isGridViewEnabled(): boolean {
    return true;
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.loadHeaderData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    super.ngOnDestroy();
  }

  public initializeColumns(): void {
    this.columnConfigs = [
      new ColumnConfig<Parameter>({
        name: nameOf<Parameter>((x) => x.id),
        displayName: 'Id',
        isSortable: false
      }),
      new ColumnConfig<Parameter>({
        name: nameOf<Parameter>((x) => x.analyteName),
        displayName: 'Analyte Name'
      }),
      new ColumnConfig<Parameter>({
        name: nameOf<Parameter>((x) => x.sampleTypeId),
        displayName: 'Sample Type',
        cellTemplate: (row: Parameter) => {
          const sampleTypeValue = row.type ?? row.sampleTypeId;
          return sampleTypeValue !== undefined && sampleTypeValue !== null
            ? this.translateService.getDynamicTranslation(SampleTypeEnum[sampleTypeValue] ?? '-')
            : '-';
        },
        enumFilterMap: this.enumToValueNameArray(SampleTypeEnum)
      }),
      new ColumnConfig<Parameter>({
        name: nameOf<Parameter>((x) => x.parameterType),
        displayName: 'Parameter Type',
        cellTemplate: (row: Parameter) => {
          if (row.parameterType === undefined || row.parameterType === null) {
            return '-';
          }

          return this.chartService.getParameterName(row.parameterType) ?? String(row.parameterType);
        },
        enumFilterMap: this.parameterTypeFilterMap
      }),
      new ColumnConfig<Parameter>({
        name: nameOf<Parameter>((x) => x.averagingType),
        displayName: 'Averaging Type',
        isSortable: false,
        cellTemplate: (row: Parameter) => {
          if (row.averagingType === undefined || row.averagingType === null) {
            return '-';
          }

          return this.averagingTypeNames[row.averagingType] ?? String(row.averagingType);
        },
        enumFilterMap: this.enumToValueNameArray(AveragingTypeEnum)
      }),
      new ColumnConfig<Parameter>({
        name: nameOf<Parameter>((x) => x.measurementMode),
        displayName: 'Measurement Mode',
        cellTemplate: (row: Parameter) => row.measurementMode === MeasurementModeEnum.Direct
            ? this.translateService.getDynamicTranslation('Direct')
            : this.translateService.getDynamicTranslation('Derived')
      }),
      new ColumnConfig<Parameter>({
        name: nameOf<Parameter>((x) => x.isNotificationOnly),
        displayName: 'Notification Only',
        isSortable: false,
        cellTemplate: (row: Parameter) => row.isNotificationOnly ? this.translateService.getDynamicTranslation('Yes') : '-'
      }),
      new ColumnConfig<Parameter>({
        name: nameOf<Parameter>((x) => x.thresholds),
        displayName: 'Thresholds',
        isSortable: false,
        cellTemplate: (row: Parameter) => row.thresholds?.length ? `${row.thresholds.length}` : '-'
      }),
      new ColumnConfig<Parameter>({
        name: 'action',
        displayName: 'Actions',
        isSortable: false,
        includeInGridSettings: false,
        isDisplayNameConfigurable: false
      })
    ];
  }

  public initializeProperties(): void {
    this.addEntityTitle = 'Parameter';
    this.searchPlaceholder = 'Search Parameters';
  }

  public initializeOverviewConfigs(): void {
    this.overviewConfigs = [];
  }

  public initializeTabs(): { routerLink: string; title: string; objectId?: string }[] {
    return realtimeConfigRouteConfig;
  }

  public getOverviewData(): Observable<void> {
    return of(undefined);
  }

  get hasOrgFilter(): boolean {
    return true;
  }

  openEntityDialog(entityId?: string): void {
    let parameter: Parameter | undefined;

    if (entityId && typeof entityId === 'string') {
      parameter = this.dataSource?.entitySubject.value?.find(p => p.id === entityId);
    }

    const dialogData: ParameterDialogData = {
      id: (parameter && parameter.id) ? parameter.id : '',
      organizationLevelId: this.selectedOrg?.id
    };

    const dialogRef = this.dialog.open(ParameterDialogComponent, {
      data: dialogData,
      width: '760px',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.toastService.showSuccess(parameter?.id ? 'Parameter updated successfully' : 'Parameter created successfully', true);
        this.loadData();
      }
    });
  }

  openEntityDeleteDialog(entity: Parameter): void {
    if (!entity.id) {
      this.toastService.displayErrorMessage('Invalid parameter', true);
      return;
    }

    this.parameterService.hasActiveSensorAssignments(entity.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (hasAssignments: boolean) => {
        if (hasAssignments) {
          this.dialog.open(ParameterDeleteBlockedDialogComponent, {
            width: '500px',
            data: { parameter: entity, initialSensorCount: 1 }
          });
        } else {
          const dialogRef = this.dialog.open(DeleteDialog, {
            data: {
              id: entity.id,
              formDeletes: true,
              service: this.parameterService,
              title: 'parameters.delete.title',
            },
            width: '440px',
            disableClose: true,
            ariaLabel: 'Parameter',
            panelClass: 'delete-confirmation-dialog',
          });

          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              this.toastService.showSuccess('Parameter deleted successfully', true);
              this.loadData();
            }
          });
        }
      },
      error: (_error) => {
        this.toastService.displayErrorMessage('Error checking sensor assignments', true);
      }
    });
  }

}

